import { Navigate, useNavigate, useSearchParams } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createSignal,
  Match,
  onCleanup,
  Show,
  Switch,
} from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { axiosInstance, queryClient } from "@/api/client";
import { useI18n } from "@/api/i18n";
import { AUTH_KEY, PROFILE_KEY } from "@/api/query-keys";
import { mapProfileSkillWriteError } from "@/api/utils/profile-skill-write-error";
import { useProfileSkillWriteErrorMessage } from "@/api/utils/profile-skill-write-error-message";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Container } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import { Progress } from "@/components/ui/progress";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useOnboardUser } from "@/domains/auth/onboarding/api";
import { LinkedInOnboarding } from "@/domains/auth/onboarding/components/LinkedInOnboarding";
import { ManualOnboarding } from "@/domains/auth/onboarding/components/ManualOnboarding";
import {
  ONBOARDING_BUILD_LOG_KEYS,
  OnboardingSuccessScreen,
} from "@/domains/auth/onboarding/components/OnboardingSuccessScreen";
import { SectorBarSummary } from "@/domains/auth/onboarding/components/steps/SectorBarSummary";
import Source from "@/domains/auth/onboarding/components/steps/Source";
import {
  createDefaultSector,
  onboardingStepsLabels,
} from "@/domains/auth/onboarding/constants";
import {
  isCurriculumSectionComplete,
  isLinkedInOnboardingComplete,
  OnboardingProvider,
  type OnboardingStore,
  useOnboardingStore,
} from "@/domains/auth/onboarding/context/OnboardingContext";
import { createProfileBuildChoreography } from "@/domains/auth/onboarding/utils/build-choreography";
import { mapOnboardingStoreToBusinessCard } from "@/domains/auth/onboarding/utils/business-card-mapper";
import { canEnterPersonalOnboarding } from "@/domains/auth/onboarding/utils/entry-guard";
import { buildOnboardingProfile } from "@/domains/auth/onboarding/utils/onboarding-profile";
import { parseEditStep } from "@/domains/auth/onboarding/utils/review-edit";
import {
  clearSectorRefusal,
  reportSectorRefusal,
} from "@/domains/auth/onboarding/utils/review-sector-error";
import { isOnboardingCategoryMissing } from "@/domains/auth/onboarding/utils/sanitize-onboarding-draft";
import { isPublisherAtSubmit } from "@/domains/auth/onboarding/utils/submit-path";
import { taxonomyReviewError } from "@/domains/auth/onboarding/utils/taxonomy-review-error";
import { ProtectedRoute } from "@/domains/auth/routes/protected-route";
import { fetchSessionBootstrap } from "@/domains/auth/session/api";
import { applySessionBootstrapToAuthStore } from "@/domains/auth/session/apply-session-bootstrap";
import {
  getNextStepPath,
  resolveOnboardingReturnPath,
} from "@/domains/auth/session/navigation";
import { BUSINESS_CARD_SLOT_COUNT } from "@/domains/freelance/components/business-card/FreelanceBusinessCard";
import type { FreelanceBusinessCardData } from "@/domains/freelance/components/business-card/types";
import {
  useGetProfileV2,
  useUploadUserResume,
} from "@/domains/freelance/profile/api";
import type {
  EducationInsertV2,
  PreviousJobInsertV2,
  ProfileV2,
} from "@/domains/freelance/profile/api/types";
import { usePostHog } from "@/hooks/usePostHog";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { parseDateOrNull } from "@/libs/date";
import { devLog } from "@/libs/dev-log";
import { tryCatch } from "@/libs/try-catch";
import type { Asset } from "@/types";

const Route = () => {
  const profile = useGetProfileV2();
  const [authStoreV2] = useAuthV2();
  // Latch owned by the guard: once the freelance celebration starts, the
  // profile legitimately turns `completed` — without this the guard would
  // redirect and pull the success screen out from under the user (AC 15).
  const [celebrating, setCelebrating] = createSignal(false);

  // Non basta lo stato del profilo personale: chi ha creato un'azienda non lo
  // completa mai, e questa schermata gli chiedeva di importare il curriculum.
  // Vedi `canEnterPersonalOnboarding`.
  const canEnter = () =>
    canEnterPersonalOnboarding({
      nextStep: authStoreV2?.next_step,
      profileCompleted: profile.data?.onboarding_status === "completed",
      celebrating: celebrating(),
    });

  return (
    <ProtectedRoute>
      <Show when={!profile.isLoading && profile.data} fallback={<Loading />}>
        <Show
          when={canEnter()}
          fallback={
            <Navigate href={getNextStepPath(authStoreV2?.next_step) ?? "/"} />
          }
        >
          <OnboardingProvider profile={profile.data!}>
            <Onboarding onCelebratingChange={setCelebrating} />
          </OnboardingProvider>
        </Show>
      </Show>
    </ProtectedRoute>
  );
};

/**
 * The success screen is a terminal state *inside* the onboarding flow, not a
 * route of its own: the guard makes a reloadable URL impossible once the
 * profile is completed (see SPEC AC 15).
 */
type OnboardingPhase = "form" | "building" | "success";

type OnboardingProps = {
  onCelebratingChange: (celebrating: boolean) => void;
};

const Onboarding = (props: OnboardingProps) => {
  const [authStoreV2, setAuthStoreV2] = useAuthV2();
  const [searchParams, setSearchParams] = useSearchParams();
  const invitationToken = () => {
    const token = searchParams.invitation_token;
    return typeof token === "string" ? token : undefined;
  };
  const sourceParam = () => {
    const source = searchParams.source;
    return typeof source === "string" ? source : undefined;
  };
  /**
   * Where the session was headed before it was sent through onboarding —
   * typically an offer an anonymous visitor tried to apply to and then chose to
   * register for (I11/EP7). `resolveSessionRedirectPath` puts it here; this is
   * the closure that spends it, and re-validates it because in between the
   * value passed through the address bar.
   */
  const returnPath = () => {
    const next = searchParams.next;
    return resolveOnboardingReturnPath(typeof next === "string" ? next : null);
  };
  const [store, setStore] = useOnboardingStore();
  const navigate = useNavigate();
  const onboardUser = useOnboardUser();
  const uploadResume = useUploadUserResume();
  const { t } = useI18n();
  const profileSkillWriteErrorMessage = useProfileSkillWriteErrorMessage();
  const { capture, identify } = usePostHog();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [phase, setPhase] = createSignal<OnboardingPhase>("form");
  const [cardData, setCardData] = createSignal<FreelanceBusinessCardData>();
  // One instance per mounted route, rewound on each submit. Re-entrancy is
  // owned by the submit button (disabled while the mutation is pending), so
  // the sequence runs exactly once per click (AC 14).
  const choreography = createProfileBuildChoreography({
    slotCount: BUSINESS_CARD_SLOT_COUNT,
    logCount: ONBOARDING_BUILD_LOG_KEYS.length,
    reducedMotion: prefersReducedMotion,
    onSuccess: () => {
      setPhase("success");
      // Fires exactly once per submit: the choreography only reaches success
      // once, no matter how many times the route re-renders.
      capture("view_onboarding_success");
    },
  });
  const stepPosition = () =>
    onboardingStepsLabels.findIndex((s) => s.step === store.step) + 1;
  const editStep = createMemo(() =>
    store.step === "review" ? parseEditStep(searchParams.edit) : null,
  );
  let scrollContainerRef: HTMLDivElement | undefined;

  // The fixed bar (its buttons, the sector summary line and the safe-area
  // inset on notched phones) grows past the 5rem the scroll area leaves for
  // it, and covers the end of the content. `barOverlap` is how many pixels of
  // the scroll area it covers: the scroll area pads that much (AC32) and the
  // sector selector keeps the secondary above it (AC6).
  const [barOverlap, setBarOverlap] = createSignal(0);
  let barRef: HTMLDivElement | undefined;
  const measureBar = () => {
    if (!barRef || !scrollContainerRef) return;
    const overlap =
      scrollContainerRef.getBoundingClientRect().bottom -
      barRef.getBoundingClientRect().top;
    setBarOverlap(Math.max(0, Math.round(overlap)));
  };
  const observeBar = (el: HTMLDivElement) => {
    barRef = el;
    measureBar();
    window.addEventListener("resize", measureBar);
    onCleanup(() => window.removeEventListener("resize", measureBar));
    if (typeof ResizeObserver === "undefined") return;
    // The bar changes height with the summary line and its wrapping.
    const observer = new ResizeObserver(measureBar);
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  };

  // Opening an inline edit section scrolls the (potentially long) review
  // screen back to the top so the step form is in view.
  createEffect(() => {
    if (editStep()) {
      scrollContainerRef?.scrollTo({ top: 0 });
    }
  });

  // Reopening the sector (from its review card or by walking back to it) ends
  // the backend's refusal shown on the card (AC21); changing the sector from
  // anywhere else ends it too, see `review-sector-error.ts`.
  createEffect(() => {
    if (store.step === "sector" || editStep() === "sector") {
      clearSectorRefusal();
    }
  });

  createEffect(() => {
    const source = sourceParam();
    if (
      source &&
      (source === "linkedin_apify" ||
        source === "manual" ||
        source === "resume")
    ) {
      setStore(
        "source",
        "value",
        source as "linkedin_apify" | "manual" | "resume",
      );
    }
  });

  const isNextDisabled = createMemo(() => {
    for (const key in store) {
      if (key === "step" || key === "profile") {
        continue;
      }
      const element =
        store[key as keyof Omit<OnboardingStore, "step" | "profile">];
      if (element && "error" in element) {
        if (element.error && key === store.step) {
          return true;
        }
      }
    }
    return false;
  });

  const isCreateProfileByLinkedInComplete = createMemo(() => {
    return isLinkedInOnboardingComplete(store);
  });

  // Manual / CV-import onboarding requires a birthdate and an uploaded CV
  // before the user can move past the Info step or create the account. The
  // resume can rehydrate from storage as a non-File, so
  // `isCurriculumSectionComplete`'s `instanceof File` check correctly treats it
  // as missing and forces a re-upload.
  const isInfoSectionComplete = createMemo(() => {
    const hasBirthdate =
      parseDateOrNull(store.info.value.date_of_birth) !== null;
    return hasBirthdate && isCurriculumSectionComplete(store);
  });

  // Senza una delle 8 categorie l'onboarding non va oltre `sector` né crea
  // l'account, in tutti e tre i percorsi (taxonomy-contract-pr3 D8,
  // freelancer-categories AC16).
  const isCategoryMissing = createMemo(() =>
    isOnboardingCategoryMissing(store),
  );

  const isNextButtonDisabled = createMemo(() => {
    if (onboardUser.isPending) {
      return true;
    }
    if (store.step === "sector" && isCategoryMissing()) {
      return true;
    }
    if (store.source.value === "linkedin_apify" && store.step !== "review") {
      return !isCreateProfileByLinkedInComplete();
    }
    if (store.step === "info") {
      return !isInfoSectionComplete();
    }
    return isNextDisabled();
  });

  const isCreateAccountDisabled = createMemo(() => {
    if (onboardUser.isPending) {
      return true;
    }
    if (isCategoryMissing()) {
      return true;
    }
    if (store.source.value === "linkedin_apify") {
      return isNextDisabled();
    }
    return !isInfoSectionComplete();
  });

  const resetOnboardingStore = () => {
    clearSectorRefusal();
    setStore(
      produce((draft) => {
        draft.step = "source";
        draft.source.value = "manual";
        draft.sector = createDefaultSector();
        draft.skills.value = [];
        draft.job_title.value = "";
        draft.experience.value = [];
        draft.education.value = [];
        draft.locale.value = {
          spoken_languages: [
            { code: "it", name: "Italian", level: "Madrelingua" },
          ],
        };
        draft.summary.value = "";
        draft.pricing.value = { price_per_day: 0 };
        draft.info.value = {
          avatar_url: "",
          residency: "",
          date_of_birth: null,
          is_available: true,
          weekly_hours: "40",
        };
        draft.online_presence.value = {
          website: "",
          portfolio: "",
          github: "",
          linkedin: "",
          instagram: "",
        };
      }),
    );
  };

  const leaveSuccessScreen = (
    destination: "/profile" | "/",
    kind: "profile" | "offers" = destination === "/profile"
      ? "profile"
      : "offers",
  ) => {
    capture("click_onboarding_success_cta", { destination: kind });
    navigate(destination);
    // Reset last: while the success screen is on screen the store still holds
    // the data the card was built from.
    resetOnboardingStore();
  };

  /** The offer that started all this, reached at last (I11/EP7). */
  const returnToDestination = (path: string) => {
    capture("click_onboarding_success_cta", { destination: "return" });
    navigate(path);
    resetOnboardingStore();
  };

  const createProfile = () => {
    // A new attempt supersedes the last refusal of the sector (AC21).
    clearSectorRefusal();
    // The publisher branch is decided from the pre-mutation session state and
    // keeps the historical flow end-to-end (AC 7).
    const isPublisher = isPublisherAtSubmit(authStoreV2);

    if (isPublisher) {
      toast.loading(t("onboarding.common.loading"), {
        id: "onboarding-create-profile",
      });
    } else {
      // The building phase *is* the loading feedback for freelancers, so the
      // loading toast is suppressed on this path.
      setCardData(mapOnboardingStoreToBusinessCard(store));
      props.onCelebratingChange(true);
      setPhase("building");
      // Rewind first so a retry after a failed submit replays the sequence.
      choreography.reset();
      choreography.start();
    }

    const jobExperience = store.experience.value.map((e) => {
      const { temp_id: _, ...rest } = e;
      const startDate = parseDateOrNull(rest.start_date);
      const endDate = parseDateOrNull(rest.end_date);
      return {
        ...rest,
        start_date: startDate!,
        end_date: endDate,
        description: rest.description ?? null,
      } satisfies PreviousJobInsertV2;
    });

    const education = store.education.value.map((e) => {
      const { temp_id: _, ...rest } = e;
      return {
        ...rest,
        start_date: parseDateOrNull(rest.start_date)!,
        end_date: parseDateOrNull(rest.end_date),
        description: rest.description ?? undefined,
      } satisfies EducationInsertV2;
    });

    const newProfile = buildOnboardingProfile(store, jobExperience);

    // @ts-expect-error - id is not defined in the UpdateProfileV2 type
    newProfile.id = undefined;

    onboardUser.mutate(
      {
        profile: newProfile,
        education,
        jobs: jobExperience,
      },
      {
        onSuccess: async () => {
          capture("end_onboarding", {
            source: store.source.value,
          });
          if (authStoreV2?.profile?.id) {
            identify(authStoreV2.profile.id, {
              onboarding_status: "completed",
            });
          }
          // Opens the success gate: the celebration still waits for the
          // choreography's minimum duration to elapse.
          choreography.resolve();

          const assets: Asset[] = [];

          if (store.info.value.resume) {
            const [response, error] = await tryCatch(
              uploadResume.mutateAsync({
                resume: store.info.value.resume,
              }),
            );
            if (error) {
              // A failed CV upload never blocks completion: the success screen
              // stays up, the user re-uploads from the profile (AC 9).
              toast.error(t("common.file_upload.error"));
            } else {
              assets.push(response.asset);
            }
          }
          if (isPublisher) {
            toast.success(t("onboarding.common.success"), {
              id: "onboarding-create-profile",
            });
          }

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [PROFILE_KEY] }),
            queryClient.invalidateQueries({ queryKey: [AUTH_KEY] }),
          ]);

          const [
            [updatedProfile, profileFetchError],
            [sessionContext, sessionError],
          ] = await Promise.all([
            tryCatch(
              queryClient.fetchQuery({
                queryKey: [PROFILE_KEY],
                queryFn: async () => {
                  const { data } = await axiosInstance.get<ProfileV2>("/me");
                  return data;
                },
              }),
            ),
            tryCatch(fetchSessionBootstrap()),
          ]);

          if (profileFetchError) {
            console.error(
              "Unable to refresh profile after onboarding:",
              profileFetchError,
            );
          }

          if (sessionError) {
            console.error(
              "Unable to refresh session after onboarding:",
              sessionError,
            );
          }

          if (!sessionContext) {
            setAuthStoreV2(
              produce((draft) => {
                draft.next_step = isPublisherAtSubmit(draft)
                  ? "publisher_dashboard"
                  : "candidate_dashboard";

                if (draft.profile) {
                  draft.profile.onboarding_status = "completed";
                  draft.profile.personal_profile_status = "completed";
                }
              }),
            );
          }

          if (updatedProfile) {
            setAuthStoreV2(
              produce((draft) => {
                const personalProfileStatus =
                  sessionContext?.profile.personal_profile_status ??
                  updatedProfile.personal_profile_status ??
                  updatedProfile.onboarding_status;
                draft.profile!.onboarding_status = personalProfileStatus;
                draft.profile!.personal_profile_status = personalProfileStatus;
                draft.profile!.weekly_hours = updatedProfile.weekly_hours;
                draft.profile!.available_from = updatedProfile.available_from;
                draft.profile!.profile_type = updatedProfile.profile_type;
                draft.profile!.payment_profile = updatedProfile.payment_profile;
                draft.profile!.assets = updatedProfile.assets;
                draft.profile!.full_name = updatedProfile.full_name;
                draft.profile!.email = updatedProfile.email;
              }),
            );
          } else {
            setAuthStoreV2(
              produce((draft) => {
                draft.profile!.onboarding_status = "completed";
                draft.profile!.personal_profile_status = "completed";
                draft.profile!.weekly_hours = newProfile.weekly_hours;
                draft.profile!.available_from = newProfile.available_from;
                draft.profile!.profile_type = newProfile.profile_type;
                draft.profile!.payment_profile = newProfile.payment_profile;
                draft.profile!.assets = assets;
                if (newProfile.full_name) {
                  draft.profile!.full_name = newProfile.full_name;
                }
                if (newProfile.email) {
                  draft.profile!.email = newProfile.email;
                }
              }),
            );
          }

          if (sessionContext) {
            applySessionBootstrapToAuthStore(setAuthStoreV2, sessionContext);
          }

          if (invitationToken()) {
            devLog("Onboarding completed for invited user");
          }

          // Freelancers stay on the celebration and leave through a CTA; only
          // the publisher branch redirects automatically (AC 6, AC 7).
          if (!isPublisher) {
            return;
          }

          const hasPublisherWorkspace = sessionContext
            ? isPublisherAtSubmit({
                capabilities: sessionContext.capabilities,
                companies: sessionContext.publisher_workspaces,
              })
            : isPublisher;
          if (hasPublisherWorkspace) {
            navigate("/profile/company");
          } else {
            navigate("/profile");
          }

          resetOnboardingStore();
        },
        onError: (error) => {
          const skillWriteError = mapProfileSkillWriteError(error);
          capture("fail_onboarding", {
            error_kind: skillWriteError.kind,
          });
          // Surface the BE's own validation message (e.g. the 18+ birthdate guard)
          // so the user can fix one field instead of re-reading a generic error.
          // A network error or a 5xx carries no such message — the mapper leaves
          // it empty rather than forward axios' English text — so it falls back
          // to the onboarding copy.
          // The onboarding store is untouched here — it is cleared only in
          // onSuccess — so resubmitting after the correction keeps every step.
          const isSkillWriteFailure =
            skillWriteError.kind === "rate_limited" ||
            skillWriteError.kind === "creation_quota" ||
            skillWriteError.kind === "claim_conflict" ||
            skillWriteError.kind === "version_conflict";
          // A 400 that names the sector or an area belongs to the «Settore»
          // card of the review, which shows the backend's text next to its
          // «Apri step»: no toast then (AC21). Everything else keeps the toast.
          const sectorRefusal = taxonomyReviewError(error);
          if (sectorRefusal) {
            reportSectorRefusal(sectorRefusal, store.sector.value);
          } else {
            toast.error(
              isSkillWriteFailure
                ? profileSkillWriteErrorMessage(skillWriteError)
                : skillWriteError.message || t("onboarding.common.error"),
              {
                id: "onboarding-create-profile",
                ...(skillWriteError.kind === "claim_conflict" ||
                skillWriteError.kind === "version_conflict"
                  ? {
                      action: {
                        label: t("common.retry"),
                        onClick: createProfile,
                      },
                    }
                  : {}),
              },
            );
          }
          if (!isPublisher) {
            // The minimum duration only gates entering success — an error
            // sends the user straight back to the review step (AC 8).
            choreography.fail();
            setPhase("form");
            props.onCelebratingChange(false);
          }
        },
      },
    );
  };

  const handleNext = () => {
    if (store.source.value === "linkedin_apify") {
      setStore("step", "review");
    } else {
      setStore("step", onboardingStepsLabels[stepPosition()].step);
    }
  };

  const handleBack = () => {
    if (editStep()) {
      setSearchParams({ edit: undefined });
      return;
    }
    if (store.source.value !== "linkedin_apify") {
      setStore("step", onboardingStepsLabels[stepPosition() - 2].step);
    } else {
      if (store.step === "review") {
        setStore("step", onboardingStepsLabels[stepPosition() - 2].step);
      } else {
        setStore("step", "source");
      }
    }
  };

  return (
    <OnboardingLayout metaTitle={t("onboarding.meta.title")}>
      <Show
        when={phase() === "form"}
        fallback={
          <Show when={cardData()}>
            {(data) => (
              <div class="h-[calc(100vh-5rem)] overflow-y-auto">
                <OnboardingSuccessScreen
                  data={data()}
                  revealedSlots={choreography.revealedSlots()}
                  logStates={choreography.logStates()}
                  succeeded={phase() === "success"}
                  animated={!prefersReducedMotion()}
                  onGoToProfile={() => leaveSuccessScreen("/profile")}
                  onExploreOffers={() => leaveSuccessScreen("/")}
                  onReturnToDestination={
                    returnPath()
                      ? () => returnToDestination(returnPath()!)
                      : undefined
                  }
                />
              </div>
            )}
          </Show>
        }
      >
        <div
          ref={scrollContainerRef}
          class="h-[calc(100vh-5rem)] mb-auto overflow-y-scroll"
        >
          <Container
            class="max-w-5xl w-full mx-auto mt-20"
            style={{ "padding-bottom": `calc(1rem + ${barOverlap()}px)` }}
          >
            <Show
              when={
                store.source.value === "manual" ||
                store.source.value === "resume"
              }
              fallback={
                <p class="text-gray-500 mb-4">
                  Ci siamo quasi! Aggiungi gli ultimi dettagli
                </p>
              }
            >
              <Show
                when={store.step !== "source"}
                fallback={<div class="h-8" />}
              >
                <p class="text-gray-500 mb-4">
                  {t(`onboarding.steps.${store.step}.label`) as string}
                </p>
              </Show>
            </Show>
            <Switch>
              <Match when={store.step === "source"}>
                <Source />
              </Match>
              <Match
                when={
                  store.source.value !== "linkedin_apify" &&
                  store.source.value !== "linkedin"
                }
              >
                <ManualOnboarding bottomInset={barOverlap()} />
              </Match>
              <Match when={store.source.value === "linkedin_apify"}>
                <LinkedInOnboarding />
              </Match>
            </Switch>
          </Container>
        </div>
      </Show>

      {/* The step chrome makes no sense once the celebration starts. */}
      <Show when={phase() === "form"}>
        <div
          ref={observeBar}
          class="w-full mt-auto border-t border-gray-200 fixed bottom-0 bg-background"
        >
          <Progress
            value={Math.round(
              (stepPosition() / onboardingStepsLabels.length) * 100,
            )}
            class="w-full rounded-none h-1"
          />
          {/* Same side padding as the page's `Container`; the bottom padding
              clears the home indicator of notched phones (AC32). */}
          <div class="max-w-5xl mx-auto px-4 lg:px-2 xl:px-0 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <Show when={store.step === "sector" || editStep() === "sector"}>
              <SectorBarSummary />
            </Show>
            <div
              class="flex items-center"
              classList={{
                "justify-end": stepPosition() === 1,
                "justify-between": stepPosition() > 1,
              }}
            >
              <Show when={stepPosition() > 1}>
                <Button variant="outline" onClick={handleBack}>
                  {t("common.back")}
                </Button>
              </Show>
              <Show
                when={store.step !== "source"}
                fallback={<div class="h-8" />}
              >
                <Show
                  when={!editStep()}
                  fallback={
                    <Button
                      disabled={isCategoryMissing()}
                      onClick={() => setSearchParams({ edit: undefined })}
                    >
                      {t("onboarding.steps.preview.backToReview")}
                    </Button>
                  }
                >
                  <Show
                    when={store.step !== "review"}
                    fallback={
                      <Button
                        disabled={isCreateAccountDisabled()}
                        onClick={createProfile}
                      >
                        {t("auth.signup.createAccount")}
                      </Button>
                    }
                  >
                    <Button
                      disabled={isNextButtonDisabled()}
                      onClick={handleNext}
                    >
                      {t("common.next")}
                    </Button>
                  </Show>
                </Show>
              </Show>
            </div>
          </div>
        </div>
      </Show>
    </OnboardingLayout>
  );
};

export default Route;
